import { defineComponent as _defineComponent } from 'vue'
import {ApiCameraWithIncludes, FloorCoords} from "@eencloud/eewc-components/src/service/api-types";
import { computed, ref, ComputedRef, reactive, withDefaults, watch, nextTick } from 'vue'
import CameraStates from '@/components/CameraStates.vue'
import PreviewImage from '@/components/PreviewImage.vue'
import { useAppStore, useCamerasStore, useFloorsStore } from '@/stores'
import { t } from '@/plugins/i18n';
import { getCurrentDate, openHistoryBrowser } from '@/service/helpers'
import router from '@/service/router'


export default /*#__PURE__*/_defineComponent({
  __name: 'CameraMarker',
  props: {
    fov: null,
    azimuth: null,
    cameraStatus: null,
    labelPosition: null,
    label: null,
    latitude: null,
    longitude: null,
    isCameraEditEnabled: { type: Boolean },
    camera: null,
    rangeInMeters: null,
    imageElementHeight: null,
    imageElementContainer: null,
    isDragging: { type: Boolean },
    hoverable: { type: Boolean, default: true }
  },
  emits: ["click", "mouseover", "dragstart", "mouseleave", "changeRangeAzimuth"],
  setup(__props: any, { emit }: { emit: ({
  (e: "click"): void,
  (e: "mouseover"): void,
  (e: "dragstart", event: DragEvent): void,
  (e: "mouseleave"): void,
  (e: "changeRangeAzimuth", changes: { azimuth: number, rangeInMeters: number }): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { fov: number, azimuth: number, cameraStatus: 'online' | 'offline', labelPosition: 'left' | 'right' | 'top' | 'bottom', label: string, latitude: number, longitude: number, isCameraEditEnabled: boolean, camera: ApiCameraWithIncludes, rangeInMeters: number, imageElementHeight: number, imageElementContainer: HTMLElement, isDragging: boolean, hoverable: boolean };





const isCameraHovered = ref(false);



// CALLBACKS
function onHoverCameraElements(cameraId: string) {
  if (props.isCameraEditEnabled || props.isDragging) {
    return;
  }
  isCameraHovered.value = true;
  emit('mouseover');
}

function onLeaveCameraElements() {
  isCameraHovered.value = false;
}

function onStartDrag(evt: DragEvent, camera: ApiCameraWithIncludes) {
  if (evt.dataTransfer) {
    const img = new Image();
    const cameraString = JSON.stringify(camera);

    evt.dataTransfer.setData("camera", cameraString);
    evt.dataTransfer.setDragImage(img, 0, 0);
  }

  emit('dragstart', evt);
}

const onClick = async () => {
  emit('click');
  await nextTick();

  if(floorsStore.isEditCamerasFloorEnabled){
    isVisiblePopup.value = false
    return;
  }

  isVisiblePopup.value = !isVisiblePopup.value;
  if (isVisiblePopup.value) {
    await setPositionOfPopup()
  }
};

const onClosePreview = async () => {
  isCameraHovered.value = false;
  await onClick()
};

// FOV

const referenceAngle = -90;

const floorYInMeters = computed(()=>{
  const lat1 = floorsStore?.currentFloor?.topLeftCoordinates.latitude || 0;
  const lat2 = floorsStore?.currentFloor?.bottomRightCoordinates.latitude || 0;
  const oneLatitudeInMeters = 111.32 * 1000;
  const distance = Math.abs(lat2 - lat1);
  return distance * oneLatitudeInMeters;
});

const currentCameraRange: ComputedRef<number> = computed(() => {
  let rangeInMeters;

  if (isEditingRangeAzimuth.value) {
    rangeInMeters = currentRangeInMeters.value;
  } else {
    rangeInMeters = props.rangeInMeters ? props.rangeInMeters : 5
  }

  const distanceInPixels = rangeInMeters / floorYInMeters.value * props.imageElementHeight;
  return distanceInPixels < 15 ? 15 : distanceInPixels;
})
const polarToCartesian = (distance: number, angleInDegrees: number) => {
  const angleInRadians = ((referenceAngle + angleInDegrees) * Math.PI) / 180.0;
  return {
    x: distance * Math.cos(angleInRadians),
    y: distance * Math.sin(angleInRadians)
  };
};
const arcStart = computed(() => {
  return polarToCartesian(currentCameraRange.value, -props.fov / 2);
})

const arcEnd = computed(() => {
  return polarToCartesian(currentCameraRange.value, props.fov / 2);
})

const largeArcFlag = computed(() => {
  return props.fov <= 180 ? "0" : "1";
})


// EDIT FOV

function useEditCameraAzimuthRange() {
  const cameraMarkerEditCenter = ref();
  const isEditingRangeAzimuth = ref(false);
  const currentAzimuth = ref(0);
  const currentRangeInMeters = ref(0);

  function getCenter(element: HTMLElement) {
    const {left, top, width, height} = element.getBoundingClientRect();
    return {x: left + width / 2, y: top + height / 2}
  }

  function startEditAzimuthRange() {
    currentAzimuth.value = props.azimuth;
    currentRangeInMeters.value = props.rangeInMeters;
    isEditingRangeAzimuth.value = true;
    document.addEventListener('mousemove', changeAzimuthRange);
    document.addEventListener('mouseup', endEditAzimuthRange);
  }

  function changeAzimuthRange(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    const {clientX, clientY} = e;
    const arrowCenter = getCenter(cameraMarkerEditCenter.value);
    const angleRad = Math.atan2(clientY - arrowCenter.y, clientX - arrowCenter.x) + Math.PI / 2;
    let rangeInPixels = Math.sqrt(
      Math.pow(clientX - arrowCenter.x, 2) +
      Math.pow(clientY - arrowCenter.y, 2)
    )

    if (rangeInPixels < 15) {
      rangeInPixels = 15;
    }

    currentAzimuth.value = (((angleRad / Math.PI) * 180) + 360) % 360
    currentRangeInMeters.value = (rangeInPixels / props.imageElementHeight) * floorYInMeters.value;
  }

  function endEditAzimuthRange(e) {
    e.preventDefault();
    e.stopPropagation();
    document.removeEventListener('mousemove', changeAzimuthRange);
    document.removeEventListener('mouseup', endEditAzimuthRange);
    emit('changeRangeAzimuth', {azimuth: currentAzimuth.value, rangeInMeters: currentRangeInMeters.value})
    isEditingRangeAzimuth.value = false;
  }
  return {startEditAzimuthRange, isEditingRangeAzimuth, currentRangeInMeters, currentAzimuth, cameraMarkerEditCenter}
}

const {
  startEditAzimuthRange,
  isEditingRangeAzimuth,
  currentRangeInMeters,
  currentAzimuth,
  cameraMarkerEditCenter
} = useEditCameraAzimuthRange();


// POPUPS

const cameraMarkerRef = ref();
const isVisiblePopup = ref(false);
const popupRef = ref();
const appStore = useAppStore();
const floorsStore = useFloorsStore();
const camerasStore = useCamerasStore();
const FOVOptions = [
  { value: 45, icon: "$icon_FOV_45" },
  { value: 360, icon: "$icon_FOV_360" },
];
const isPopupBellow = ref(false);



watch(() => floorsStore.isEditCamerasFloorEnabled, () => {
  if(floorsStore.isEditCamerasFloorEnabled){
    isVisiblePopup.value = false
  }
})

async function setPositionOfPopup() {
  await nextTick();
  const popupHalfWidth = 130;
  const popupHeight = 250;
  let leftMargin = 0;

  const popupElement = popupRef.value;
  const hoveredMarker = cameraMarkerRef.value;
  const markerPosition = hoveredMarker && hoveredMarker.getBoundingClientRect();
  const imageElementContainerPosition = props.imageElementContainer && props.imageElementContainer.getBoundingClientRect();

  if (!markerPosition || !imageElementContainerPosition || !popupElement) {
    return;
  }

  const relativeMarkerLeftPosition = markerPosition.left - imageElementContainerPosition.left;
  const relativeMarkerRightPosition = imageElementContainerPosition.right - markerPosition.right;
  const relativeMarkerTopPosition = markerPosition.top - imageElementContainerPosition.top;
  const noSpaceOnRight = relativeMarkerRightPosition < popupHalfWidth;
  const noSpaceOnLeft = relativeMarkerLeftPosition < popupHalfWidth;

  if (noSpaceOnRight) {
    leftMargin = relativeMarkerRightPosition - popupHalfWidth;
  }

  if (noSpaceOnLeft) {
    leftMargin = popupHalfWidth - relativeMarkerLeftPosition;
  }

  isPopupBellow.value = relativeMarkerTopPosition < popupHeight;
  popupElement.style.left = `${leftMargin}px`;
}

async function changeCameraFOV(camera: ApiCameraWithIncludes, fieldOfView: number ) {
  appStore.loading = true;

  try{
    await camerasStore.editCamera({
      cameraId: camera.id,
      devicePosition: { fieldOfView: fieldOfView }
    })
  } catch (error) { /* empty */ }

  appStore.loading = false;
}

const menuOptions = [
  {
    name: t('History'),
    disabled: false,
    value: 'open-history-browser'
  },
]

function dropDownMenuAction(action: string, cameraId: string) {
  switch (action) {
    case "open-history-browser": {
      openHistoryBrowser(appStore.isNewWebapp, cameraId)
    }
  }
}

const currentRoute = computed(() => router.currentRoute.path);

function showLiveView(cameraId?: string) {
  cameraId && router.push(`${currentRoute.value}/video/${cameraId}`)
}

return { __sfc: true,emit, props, isCameraHovered, onHoverCameraElements, onLeaveCameraElements, onStartDrag, onClick, onClosePreview, referenceAngle, floorYInMeters, currentCameraRange, polarToCartesian, arcStart, arcEnd, largeArcFlag, useEditCameraAzimuthRange, startEditAzimuthRange, isEditingRangeAzimuth, currentRangeInMeters, currentAzimuth, cameraMarkerEditCenter, cameraMarkerRef, isVisiblePopup, popupRef, appStore, floorsStore, camerasStore, FOVOptions, isPopupBellow, setPositionOfPopup, changeCameraFOV, menuOptions, dropDownMenuAction, currentRoute, showLiveView, CameraStates, PreviewImage, getCurrentDate }
}

})